import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { formatDate } from '@angular/common';
import * as FileSaver from 'file-saver';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

import { LoadRecurrents, LoadRecurrentsFailure, LoadRecurrentsSuccess, RecurrentActionTypes } from '../../store/actions/recurrent.actions';
import { SummaryRecurrentModel } from '../../models/summary-recurrent.model';
import { RecurrentService } from '../../services/recurrent.service';
import { createRecurrentTableConfig, TableRowAction } from './recurrent-table.config';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { PreinvoiceService } from '../../../../../modules/invoice/preinvoice/preinvoice.service';
import { InvoiceService } from '../../../../../modules/invoice/invoice/invoice.service';

@Component({
  standalone: false,
  selector: 'app-recurrent-list-v2',
  templateUrl: './recurrent-list.components.html',
  styleUrls: ['./recurrent-list.components.css']
})
export class RecurrentListV2Component implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table') t1!: CustomTableComponent;
  @ViewChild('invoiceSummary') invoiceSummary!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;
  detailLoader = (uid: string) => this.recurrentService.getDetailByUid(uid);

  icon = 'autorenew';
  title = 'Recurrentes';
  subTitle = 'Consulta y gestiona tus facturas recurrentes';

  summary: any = null;

  monthsControl: FormControl = new FormControl([]);
  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private destroy$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private recurrentService: RecurrentService,
    private preinvoiceService: PreinvoiceService,
    private invoiceService: InvoiceService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewSummaryRecurrentFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored);
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) { defaultFilter = {}; }
    }

    this.tableConfig = createRecurrentTableConfig(
      {
        onCreate: () => this.createRecurrent(),
        actionMap: {
          [TableRowAction.Pdf]: (row) => this.pdf(row),
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
        },
        selectable: false,
        summaryTemplate: this.invoiceSummary,
        columns: [
          { key: 'recurrentNumber', label: 'Factura recurrente', sortable: true, sortKey: 'RecurrentNumber', onCellClick: (row) => this.pdf(row) },
          { key: 'receiverName', label: 'Destinatario', sortable: true, sortKey: 'ReceiverName', onCellClick: (row) => this.edit(row) },
          { key: 'receiverVatNumber', label: 'CIF/NIF', sortable: true, sortKey: 'ReceiverVatNumber' },
          { key: 'invoiceRelease', label: 'Fecha', sortable: true, sortKey: 'InvoiceRelease', formatFn: (val) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '' },
          { key: 'recurrentStart', label: 'Inicio', sortable: true, sortKey: 'RecurrentStart', formatFn: (val) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '' },
          { key: 'recurrentFrequency', label: 'Frecuencia', sortable: true, sortKey: 'RecurrentFrequency' },
          { key: 'paymentMethodInfo', label: 'Método de cobro', sortable: true, sortKey: 'TypePay' },
          { key: 'total', label: 'Total', sortable: true, sortKey: 'Total', formatFn: (val) => Utils.FormatMoney(val) + ' €' },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.createRecurrent() },
        ],
        rowConfig: {},
      },
      {
        filterView: 'viewSummaryRecurrentFilters',
        defaultFilter,
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };

    this.subs.push(
      this.actions$
        .pipe(
          ofType<LoadRecurrentsSuccess>(RecurrentActionTypes.LoadRecurrentsSuccess),
          map(action => action.payload)
        )
        .subscribe(res => {
          const expanded = new Set((this.t1.tableState.snapshot.data || []).filter((r: any) => r.__expanded).map((r: any) => r.uid));
          const items = (res.items || []).map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
          this.summary = res.summary;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(res.total);
          this.t1.tableState.setLoading(false);
        })
    );

    this.subs.push(
      this.actions$
        .pipe(ofType<LoadRecurrentsFailure>(RecurrentActionTypes.LoadRecurrentsFailure))
        .subscribe(() => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar las facturas recurrentes');
        })
    );
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.invoiceSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.monthsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        const raw = Array.isArray(value) ? value : [];
        const clean = raw.filter((v: any) => typeof v === 'number');
        if (clean.length !== raw.length) {
          this.monthsControl.setValue(clean, { emitEvent: false });
        }
        this.t1.tableState.patchFilters({ months: clean });
      });

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(s => ((s.filters && (s.filters as any).months) || []) as number[]),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this.destroy$),
      )
      .subscribe(months => this.monthsControl.setValue(months, { emitEvent: false }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewSummaryRecurrentFilters', JSON.stringify(state));
        this.dispatchLoad();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  createRecurrent(): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.router.navigate(['/invoice/recurrents/create']);
  }

  edit(row: SummaryRecurrentModel): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.router.navigate(['/invoice/recurrents/edit', row.uid]);
  }

  pdf(row: SummaryRecurrentModel): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Generando PDF…', this.preinvoiceService.pdf(row.uid, consts.EMPTY_GUID))
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetPdfName('Factura_Recurrente_' + (row.recurrentNumber || row.uid)));
        this.notify.ok('PDF generado correctamente');
      })
      .catch(() => this.notify.error('No se pudo descargar el PDF'));
  }

  delete(row: SummaryRecurrentModel): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar factura recurrente',
      subtitle: row.recurrentNumber || undefined,
      message: '¿Seguro que quieres eliminar esta factura recurrente? Dejarán de emitirse las facturas programadas. Las facturas ya emitidas no se borran.',
      tone: 'danger',
      details: this.recurrentDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando factura recurrente…',
      errorText: 'No se pudo eliminar la factura recurrente',
      action: () => this.invoiceService.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Factura recurrente eliminada correctamente');
      this.dispatchLoad();
    });
  }

  private recurrentDetails(row: SummaryRecurrentModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.receiverName) details.push({ label: 'Destinatario', value: row.receiverName });
    if (row.recurrentFrequency) details.push({ label: 'Frecuencia', value: row.recurrentFrequency });
    if (row.recurrentStart) details.push({ label: 'Inicio', value: formatDate(row.recurrentStart, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.total) + ' €' });
    return details;
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private dispatchLoad(): void {
    const filter = { ...this.lastState, filters: { ...this.lastState.filters, year: ActualSelected.yearSelected } };
    this.t1.tableState.setLoading(true);
    this.store.dispatch(new LoadRecurrents({ filter: buildSearchPayload(filter) }));
  }
}
